/**
 * Secure API Client for Command Center
 * This is the ONLY service that communicates with the backend
 *
 * Features:
 * - Token expiration checking and refresh
 * - Structured error handling
 * - Automatic retry on token expiration
 */

import axios, { AxiosError, AxiosRequestConfig } from 'axios';

// =============================================================================
// Types
// =============================================================================

// Tab permission structure
export interface TabPermissions {
  view?: boolean;
  edit?: boolean;
  upload?: boolean;
  sync?: boolean;
  publish?: boolean;
}

// All tabs and their permissions
export interface UserPermissions extends Record<'feature_flags' | 'staff', TabPermissions> {
  dashboard: TabPermissions;
  services: TabPermissions;
  permissions: TabPermissions;
  service_discovery: TabPermissions;
  settings: TabPermissions;
  audit_log: TabPermissions;
  generic_terms: TabPermissions;
  onboarding_survey: TabPermissions;
  user_management: TabPermissions;
  content: TabPermissions;
}

export interface User {
  email: string;
  name: string;
  role: 'viewer' | 'developer' | 'admin';
  domain: string;
  created_at: string;
  last_login: string;
  token_expires_at?: number; // Unix timestamp from backend
  effective_permissions?: UserPermissions; // Computed permissions
  permissions?: Partial<UserPermissions>; // Custom permissions (if set)
  has_custom_permissions?: boolean;
}

export interface ServiceDeployment {
  image_tag: string;
  revision: string;
  deployed_at: string;
  deployed_by: string;
  resources?: {
    memory?: string;
    cpu?: string;
    timeout_seconds?: number;
    max_instances?: number;
  };
}

export interface ServiceRuntime {
  version?: string;
  health_status: 'healthy' | 'unhealthy' | 'unreachable';
  transport?: string;
  last_health_check?: string;
  error?: string;
}

export interface Service {
  id: string;
  name: string;
  url: string;
  type: 'mcp_server' | 'agent' | 'api' | 'service' | 'unknown';
  description?: string;
  active: boolean;
  endpoints?: Record<string, string>;
  deployment?: ServiceDeployment;
  runtime?: ServiceRuntime;
  used_by?: string[];
  updated_at?: string;
  updated_by?: string;
}

export interface ServicesResponse {
  services: Service[];
  last_synced: string | null;
  sync_performed: boolean;
}

export interface Agent {
  name: string;
  services: string[];
  updated_by: string;
  updated_at: string;
}

export interface AuditLog {
  id: string;
  timestamp: string;
  user: {
    email: string;
    ip_address: string;
    user_agent: string;
  };
  action: string;
  resource: {
    type: string;
    id: string;
  };
  details: any;
  environment: string;
}

// Match review queue (place_id verify escalates/rejects awaiting human review)
export interface MatchReviewItem {
  id: string | null;
  entity_type: string;
  entity_id: string;
  reason: string;
  signals: Record<string, any> | null;
  status: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  notes: string | null;
  created_at: string | null;
  // place context (joined from global_locations) so a reviewer can decide
  entity_name?: string | null;
  entity_city?: string | null;
  entity_address?: string | null;
  entity_country?: string | null;
  entity_lat?: number | null;
  entity_lng?: number | null;
  entity_place_id?: string | null;
}

export interface MatchReviewQueueResponse {
  items: MatchReviewItem[];
  count: number;
  total: number;
  status: string;
  limit: number;
  offset: number;
}

// Onboarding Survey Types (matches Firebase snake_case)
export interface QuestionOption {
  id: string;
  ui_label: string;
  firebase_value: string;
  salesforce_value: string;
  icon_url?: string | null;
}

export interface OverlayData {
  text: string;
  attribution: string;
  avatar_url: string | null;
}

export interface PageContent {
  media_type?: 'video' | 'image';
  media_url: string | null;
  media_poster_url: string | null;
  poster_source?: 'auto' | 'custom';
  overlay: OverlayData;
}

export interface OnboardingQuestion {
  id: string;
  order: number;
  question_text: string;
  question_text_highlight?: string | null;
  input_type: 'checkbox' | 'radio' | 'text' | 'date' | 'number' | 'textarea';
  is_required: boolean;
  salesforce_field: string;
  salesforce_field_type?: string;
  page_content: PageContent;
  options?: QuestionOption[] | null;
  routing?: any | null;
  show_if?: any | null;
}

export interface OnboardingScreen {
  enabled: boolean;
  title?: string;
  title_highlight?: string;
  subtitle?: string;
  button_text?: string;
  page_content?: PageContent;
}

export interface OnboardingSurveyConfig {
  id?: string;
  version: string;
  last_updated: string;
  updated_by?: {
    email: string;
    name: string;
  };
  restored_from?: {
    version_id: string;
    original_version: string;
  };
  start_screen?: OnboardingScreen;
  end_screen?: OnboardingScreen;
  questions: OnboardingQuestion[];
}

export interface OnboardingHistoryEntry {
  id: string;
  version: string;
  archived_at: string;
  archived_by: {
    email: string;
    name: string;
  };
  change_description?: string;
  questions_changed: number[];
  config_snapshot?: OnboardingSurveyConfig;
}

export interface OnboardingUploadResponse {
  upload_url: string;
  public_url: string;
  blob_path: string;
  expires_in_minutes: number;
}

// Copy from Staging types (Production only)
export interface StagingFile {
  path: string;
  size: number;
  content_type: string;
  updated: string | null;
  public_url: string;
}

export interface StagingPreviewResponse {
  staging_bucket: string;
  files: StagingFile[];
  file_count: number;
}

export interface CopyFromStagingResponse {
  success: boolean;
  message: string;
  copied: Array<{ path: string; size: number; content_type: string }>;
  skipped: Array<{ path: string; reason: string }>;
  failed: Array<{ path: string; error: string }>;
  summary: {
    copied_count: number;
    skipped_count: number;
    failed_count: number;
  };
}

// Structured error from backend
export interface APIErrorResponse {
  error: {
    code: string;
    message: string;
    details?: Record<string, any>;
  };
}

// Error codes from backend
export enum ErrorCode {
  AUTH_MISSING_TOKEN = 'AUTH_MISSING_TOKEN',
  AUTH_INVALID_TOKEN = 'AUTH_INVALID_TOKEN',
  AUTH_TOKEN_EXPIRED = 'AUTH_TOKEN_EXPIRED',
  AUTH_UNAUTHORIZED_DOMAIN = 'AUTH_UNAUTHORIZED_DOMAIN',
  AUTH_INSUFFICIENT_PERMISSIONS = 'AUTH_INSUFFICIENT_PERMISSIONS',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  NOT_FOUND = 'NOT_FOUND',
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
  BAD_REQUEST = 'BAD_REQUEST',
}

// Custom error class for API errors
export class APIError extends Error {
  code: string;
  status: number;
  details?: Record<string, any>;

  constructor(code: string, message: string, status: number, details?: Record<string, any>) {
    super(message);
    this.name = 'APIError';
    this.code = code;
    this.status = status;
    this.details = details;
  }

  get isTokenExpired(): boolean {
    return this.code === ErrorCode.AUTH_TOKEN_EXPIRED;
  }

  get isAuthError(): boolean {
    return [
      ErrorCode.AUTH_MISSING_TOKEN,
      ErrorCode.AUTH_INVALID_TOKEN,
      ErrorCode.AUTH_TOKEN_EXPIRED,
      ErrorCode.AUTH_UNAUTHORIZED_DOMAIN,
    ].includes(this.code as ErrorCode);
  }
}

// =============================================================================
// Configuration
// =============================================================================

const API_BASE_URL =
  import.meta.env.VITE_BACKEND_API_URL ||
  'https://command-center-backend-api-55189446448.us-central1.run.app';

// Token refresh buffer (refresh 5 minutes before expiry)
const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000;

// =============================================================================
// Token Management
// =============================================================================

interface TokenInfo {
  token: string;
  expiresAt: number; // Unix timestamp in seconds
}

let tokenInfo: TokenInfo | null = null;
let tokenRefreshCallback: (() => Promise<string | null>) | null = null;

/**
 * Check if the current token is expired or about to expire
 */
function isTokenExpired(): boolean {
  if (!tokenInfo) return true;

  const now = Date.now();
  const expiresAtMs = tokenInfo.expiresAt * 1000;

  // Consider expired if within buffer time
  return now >= expiresAtMs - TOKEN_REFRESH_BUFFER_MS;
}

/**
 * Parse JWT to extract expiration time
 */
function parseJwtExpiration(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = JSON.parse(atob(parts[1]));
    return payload.exp || null;
  } catch {
    return null;
  }
}

// =============================================================================
// Axios Instance
// =============================================================================

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor - add auth token and check expiration
apiClient.interceptors.request.use(async (config) => {
  // Check if token needs refresh before making request
  if (tokenInfo && isTokenExpired() && tokenRefreshCallback) {
    console.log('Token expired or expiring soon, attempting refresh...');
    try {
      const newToken = await tokenRefreshCallback();
      if (newToken) {
        const exp = parseJwtExpiration(newToken);
        if (exp) {
          tokenInfo = { token: newToken, expiresAt: exp };
          sessionStorage.setItem('google_auth_token', newToken);
          console.log('Token refreshed successfully');
        }
      }
    } catch (error) {
      console.error('Failed to refresh token:', error);
      // Continue with old token, let backend reject if expired
    }
  }

  if (tokenInfo?.token) {
    config.headers.Authorization = `Bearer ${tokenInfo.token}`;
  }

  return config;
});

// Response interceptor - handle errors
apiClient.interceptors.response.use(
  (response) => {
    // Check if response is HTML (indicates fallback to index.html)
    if (
      typeof response.data === 'string' &&
      response.data.trim().startsWith('<!DOCTYPE html')
    ) {
      throw new APIError(
        'PROXY_ERROR',
        'Received HTML response instead of JSON. The API proxy may be misconfigured.',
        502
      );
    }
    return response;
  },
  (error: AxiosError<APIErrorResponse>) => {
    return Promise.reject(error);
  }
);

// =============================================================================
// Error Handler
// =============================================================================

export function handleApiError(error: AxiosError<APIErrorResponse>): never {
  console.error('API Error:', error.response?.status, error.response?.data);

  // Handle structured error response from backend
  if (error.response?.data?.error) {
    const { code, message, details } = error.response.data.error;
    throw new APIError(code, message, error.response.status, details);
  }

  // Handle legacy error format
  if (error.response?.status === 401) {
    throw new APIError(
      ErrorCode.AUTH_INVALID_TOKEN,
      'Authentication failed. Please sign in again.',
      401
    );
  }

  if (error.response?.status === 403) {
    throw new APIError(
      ErrorCode.AUTH_INSUFFICIENT_PERMISSIONS,
      'Access denied. Insufficient permissions.',
      403
    );
  }

  if (error.response?.status === 404) {
    throw new APIError(ErrorCode.NOT_FOUND, 'Resource not found', 404);
  }

  if (error.response?.status === 502 || error.response?.status === 503) {
    throw new APIError(
      ErrorCode.SERVICE_UNAVAILABLE,
      'Service temporarily unavailable. Please try again.',
      error.response.status
    );
  }

  // Network error or unknown
  throw new APIError(
    ErrorCode.INTERNAL_ERROR,
    error.message || 'An unexpected error occurred',
    error.response?.status || 500
  );
}

// =============================================================================
// API Methods
// =============================================================================

export const api = {
  // ---------------------------------------------------------------------------
  // Token Management
  // ---------------------------------------------------------------------------

  /**
   * Set the auth token with expiration tracking
   */
  setAuthToken(token: string) {
    const exp = parseJwtExpiration(token);
    tokenInfo = {
      token,
      expiresAt: exp || Math.floor(Date.now() / 1000) + 3600, // Default 1 hour
    };
  },

  /**
   * Clear the auth token
   */
  clearAuthToken() {
    tokenInfo = null;
  },

  /**
   * Set callback for token refresh
   * This should be called by GoogleAuthContext to provide refresh capability
   */
  setTokenRefreshCallback(callback: () => Promise<string | null>) {
    tokenRefreshCallback = callback;
  },

  /**
   * Check if token is expired (for external use)
   */
  isTokenExpired(): boolean {
    return isTokenExpired();
  },

  /**
   * Get token expiration time (for UI display)
   */
  getTokenExpiresAt(): number | null {
    return tokenInfo?.expiresAt || null;
  },

  // ---------------------------------------------------------------------------
  // Auth
  // ---------------------------------------------------------------------------

  async getCurrentUser(): Promise<User> {
    try {
      const response = await apiClient.get<User>('/api/v1/profile');
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  // ---------------------------------------------------------------------------
  // Services
  // ---------------------------------------------------------------------------

  async getServices(options?: { sync?: boolean; includeUsedBy?: boolean }): Promise<ServicesResponse> {
    try {
      const params = new URLSearchParams();
      if (options?.sync) params.append('sync', 'true');
      if (options?.includeUsedBy) params.append('include_used_by', 'true');

      const url = params.toString() ? `/api/v1/services?${params}` : '/api/v1/services';
      const response = await apiClient.get<ServicesResponse>(url);
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async getService(serviceId: string): Promise<Service> {
    try {
      const response = await apiClient.get<Service>(`/api/v1/services/${serviceId}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async updateService(serviceId: string, data: Partial<Service>): Promise<void> {
    try {
      await apiClient.put(`/api/v1/services/${serviceId}`, data);
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async syncServicesMetadata(): Promise<{ synced_count: number; services: string[]; timestamp: string }> {
    try {
      const response = await apiClient.post('/api/v1/services/sync');
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  // ---------------------------------------------------------------------------
  // Agents
  // ---------------------------------------------------------------------------

  async getAgent(name: string): Promise<Agent> {
    try {
      const response = await apiClient.get<Agent>(`/api/v1/agents/${name}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async updateAgentPermissions(name: string, services: string[]): Promise<void> {
    try {
      await apiClient.post(`/api/v1/agents/${name}/permissions`, { services });
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async getAgentPermissions(): Promise<any> {
    try {
      const response = await apiClient.get('/api/v1/agents/permissions');
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  // ---------------------------------------------------------------------------
  // Users
  // ---------------------------------------------------------------------------

  async getUsers(): Promise<User[]> {
    try {
      const response = await apiClient.get<{ users: User[] }>('/api/v1/users');
      return response.data.users;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async updateUserRole(email: string, role: User['role']): Promise<void> {
    try {
      await apiClient.put(`/api/v1/users/${email}/role`, { role });
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async inviteUser(email: string, role: User['role'], message?: string): Promise<void> {
    try {
      await apiClient.post('/api/v1/users/invite', { email, role, message });
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async deleteUser(email: string): Promise<void> {
    try {
      await apiClient.delete(`/api/v1/users/${email}`);
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  // ---------------------------------------------------------------------------
  // Audit Logs
  // ---------------------------------------------------------------------------

  async getAuditLogs(limit: number = 100): Promise<AuditLog[]> {
    try {
      const response = await apiClient.get<{ logs: AuditLog[] }>('/api/v1/audit-logs', {
        params: { limit },
      });
      return response.data.logs;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  // ---------------------------------------------------------------------------
  // Match Review Queue (proxies ERS /api/v1/match-review-queue)
  // ---------------------------------------------------------------------------

  async getMatchReviewQueue(
    params: {
      status?: string;
      entityType?: string;
      limit?: number;
      offset?: number;
    } = {}
  ): Promise<MatchReviewQueueResponse> {
    try {
      const response = await apiClient.get<MatchReviewQueueResponse>(
        '/api/v1/match-review-queue',
        {
          params: {
            status: params.status ?? 'pending',
            entity_type: params.entityType,
            limit: params.limit ?? 50,
            offset: params.offset ?? 0,
          },
        }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async updateMatchReviewItem(payload: {
    entity_type: string;
    entity_id: string;
    reason: string;
    status: string;
    notes?: string;
  }): Promise<{ updated: boolean; status: string }> {
    // reviewed_by is injected server-side (the authed operator), never sent here.
    try {
      const response = await apiClient.patch<{ updated: boolean; status: string }>(
        '/api/v1/match-review-queue',
        payload
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  // ---------------------------------------------------------------------------
  // Health
  // ---------------------------------------------------------------------------

  async checkHealth(): Promise<boolean> {
    try {
      const response = await apiClient.get('/health');
      return response.data.status === 'healthy';
    } catch {
      return false;
    }
  },

  // ---------------------------------------------------------------------------
  // Discovery / Service Registry
  // ---------------------------------------------------------------------------

  async getDiscoveryConfig(): Promise<any> {
    try {
      const response = await apiClient.get('/api/v1/discovery/config');
      return response.data.config || response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async updateDiscoveryConfig(config: any): Promise<void> {
    try {
      await apiClient.post('/api/v1/discovery/config', config);
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async triggerManualRefresh(): Promise<{ refreshed: boolean; servicesFound: number }> {
    try {
      const response = await apiClient.post('/api/v1/discovery/refresh');
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  // ---------------------------------------------------------------------------
  // Access Requests
  // ---------------------------------------------------------------------------

  async requestAccess(requestedRole: string, reason: string): Promise<any> {
    try {
      const response = await apiClient.post('/api/v1/request-access', {
        requested_role: requestedRole,
        reason,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  // ---------------------------------------------------------------------------
  // User Permissions API (Fine-Grained Access Control)
  // ---------------------------------------------------------------------------

  async getPermissionsSchema(): Promise<{
    tabs: Record<string, string[]>;
    default_by_role: Record<string, UserPermissions>;
  }> {
    try {
      const response = await apiClient.get('/api/v1/permissions/schema');
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async getUserPermissions(email: string): Promise<{
    email: string;
    role: string;
    custom_permissions: Partial<UserPermissions> | null;
    effective_permissions: UserPermissions;
    has_custom_permissions: boolean;
  }> {
    try {
      const response = await apiClient.get(`/api/v1/users/${encodeURIComponent(email)}/permissions`);
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async updateUserPermissions(
    email: string,
    permissions: Partial<UserPermissions> | null
  ): Promise<{
    success: boolean;
    email: string;
    effective_permissions: UserPermissions;
    has_custom_permissions: boolean;
  }> {
    try {
      const response = await apiClient.put(`/api/v1/users/${encodeURIComponent(email)}/permissions`, {
        permissions,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  // ---------------------------------------------------------------------------
  // Settings API
  // ---------------------------------------------------------------------------

  async getSettingsModules(): Promise<string[]> {
    try {
      const response = await apiClient.get<{ modules: string[] }>('/api/v1/settings/modules');
      return response.data.modules;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async getModuleConfig(moduleId: string): Promise<any> {
    try {
      const response = await apiClient.get<any>(`/api/v1/settings/modules/${moduleId}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async updateModuleConfig(moduleId: string, config: any): Promise<void> {
    try {
      await apiClient.put(`/api/v1/settings/modules/${moduleId}`, config);
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  // Background-search provider quota status (CC backend proxies ERS usage).
  // ``refresh`` forces a live poll of the vendors' FREE usage endpoints so the
  // panel's "last refreshed" timestamps advance on demand (no search quota spent).
  async getBackgroundSearchStatus(refresh = false): Promise<any> {
    try {
      const response = await apiClient.get<any>('/api/v1/background-search/status', {
        params: refresh ? { refresh: 'true' } : undefined,
        timeout: refresh ? 40000 : undefined,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  // Spend circuit breaker — operator "Authorize next tranche" (developer-gated).
  // Proxies to ERS via the CC backend; ERS owns the ratchet + hard-cap policy and
  // returns the old/new ceiling (or a 400 when already at the hard cap).
  async authorizeBudgetTranche(
    provider: string,
    step?: number
  ): Promise<{ oldCeiling: number; newCeiling: number; hardCap: number; atHardCap: boolean }> {
    try {
      const response = await apiClient.post('/api/v1/background-search/authorize-tranche', {
        provider,
        ...(step != null ? { step } : {}),
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async getChannelProfiles(): Promise<any[]> {
    try {
      const response = await apiClient.get<{ channels: any[] }>('/api/v1/settings/channels');
      return response.data.channels;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async getChannelProfile(channelId: string): Promise<any> {
    try {
      const response = await apiClient.get<any>(`/api/v1/settings/channels/${channelId}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async updateChannelProfile(channelId: string, profile: any): Promise<void> {
    try {
      await apiClient.put(`/api/v1/settings/channels/${channelId}`, profile);
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  // ---------------------------------------------------------------------------
  // Onboarding Survey API
  // ---------------------------------------------------------------------------

  async getOnboardingConfig(): Promise<OnboardingSurveyConfig> {
    try {
      const response = await apiClient.get<OnboardingSurveyConfig>('/api/v1/onboarding/config');
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async updateOnboardingConfig(
    config: OnboardingSurveyConfig,
    changeDescription?: string
  ): Promise<{ success: boolean; version: string; questions_changed: number[] }> {
    try {
      const response = await apiClient.put('/api/v1/onboarding/config', {
        config,
        change_description: changeDescription || '',
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async getOnboardingHistory(limit?: number): Promise<OnboardingHistoryEntry[]> {
    try {
      const params = limit ? { limit } : {};
      const response = await apiClient.get<{ history: OnboardingHistoryEntry[] }>(
        '/api/v1/onboarding/history',
        { params }
      );
      return response.data.history;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async getOnboardingHistoryVersion(versionId: string): Promise<OnboardingHistoryEntry> {
    try {
      const response = await apiClient.get<OnboardingHistoryEntry>(
        `/api/v1/onboarding/history/${versionId}`
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async restoreOnboardingVersion(
    versionId: string
  ): Promise<{ success: boolean; version: string; restored_from: string }> {
    try {
      const response = await apiClient.post(`/api/v1/onboarding/restore/${versionId}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async getOnboardingUploadUrl(
    filename: string,
    contentType: string,
    assetType: 'video' | 'poster' | 'avatar'
  ): Promise<OnboardingUploadResponse> {
    try {
      const response = await apiClient.post<OnboardingUploadResponse>('/api/v1/onboarding/upload', {
        filename,
        content_type: contentType,
        asset_type: assetType,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async listAssetVersions(assetPath?: string, assetType?: string): Promise<{ versions: AssetVersion[]; count: number }> {
    try {
      const params: Record<string, string> = {};
      if (assetPath) params.asset_path = assetPath;
      if (assetType) params.asset_type = assetType;
      const response = await apiClient.get<{ versions: AssetVersion[]; count: number }>('/api/v1/onboarding/versions', { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async restoreAssetVersion(versionId: string): Promise<{ success: boolean; message: string; public_url: string }> {
    try {
      const response = await apiClient.post(`/api/v1/onboarding/versions/${versionId}/restore`);
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async deleteAssetVersion(versionId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.delete(`/api/v1/onboarding/versions/${versionId}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  // ---------------------------------------------------------------------------
  // Copy from Staging API (Production only)
  // ---------------------------------------------------------------------------

  async getStagingPreview(): Promise<StagingPreviewResponse> {
    try {
      const response = await apiClient.get<StagingPreviewResponse>('/api/v1/onboarding/staging-preview');
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async copyFromStaging(): Promise<CopyFromStagingResponse> {
    try {
      const response = await apiClient.post<CopyFromStagingResponse>('/api/v1/onboarding/copy-from-staging');
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  // ---------------------------------------------------------------------------
  // Generic Terms API
  // ---------------------------------------------------------------------------

  async getGenericTerms(status?: string): Promise<{ terms: any[]; count: number }> {
    try {
      const params = status && status !== 'all' ? { status } : {};
      const response = await apiClient.get<{ terms: any[]; count: number }>(
        '/api/v1/generic-terms',
        { params }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async updateGenericTerm(
    termId: string,
    status: 'confirmed_generic' | 'confirmed_valid'
  ): Promise<{ success: boolean; term_id: string; status: string }> {
    try {
      const response = await apiClient.put(`/api/v1/generic-terms/${termId}`, {
        status,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  // ---------------------------------------------------------------------------
  // Media Library API
  // ---------------------------------------------------------------------------

  async listMediaFiles(path: string = ''): Promise<MediaListResponse> {
    try {
      const response = await apiClient.get<MediaListResponse>('/api/v1/media/list', {
        params: { path },
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async getMediaUploadUrl(
    filename: string,
    contentType: string,
    folder: string = 'General'
  ): Promise<MediaUploadResponse> {
    try {
      const response = await apiClient.post<MediaUploadResponse>('/api/v1/media/upload', {
        filename,
        content_type: contentType,
        folder,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async createMediaFolder(name: string, parentPath: string = ''): Promise<{ success: boolean; folder_path: string; message: string }> {
    try {
      const response = await apiClient.post('/api/v1/media/folder', {
        name,
        parent_path: parentPath,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async deleteMediaFile(path: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.delete('/api/v1/media/file', {
        params: { path },
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async deleteMediaFolder(path: string): Promise<{ success: boolean; message: string; deleted_count: number }> {
    try {
      const response = await apiClient.delete('/api/v1/media/folder', {
        params: { path },
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  // ---------------------------------------------------------------------------
  // Pipeline Management API
  // ---------------------------------------------------------------------------

  async getPipelineConfig(): Promise<PipelineConfig> {
    try {
      const response = await apiClient.get<PipelineConfig>('/api/v1/pipeline/config');
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async updatePipelineConfig(config: Partial<PipelineConfig>): Promise<{ success: boolean }> {
    try {
      const response = await apiClient.put('/api/v1/pipeline/config', config);
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async getPipelineRuns(params?: {
    limit?: number;
    status?: PipelineRunStatus;
    environment?: PipelineEnvironment;
  }): Promise<{ runs: PipelineRun[] }> {
    try {
      const response = await apiClient.get<{ runs: PipelineRun[] }>('/api/v1/pipeline/runs', {
        params,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async getPipelineRun(runId: string): Promise<PipelineRun> {
    try {
      const response = await apiClient.get<PipelineRun>(`/api/v1/pipeline/runs/${runId}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async getPipelineProgress(runId: string): Promise<PipelineProgress> {
    try {
      const response = await apiClient.get<PipelineProgress>(`/api/v1/pipeline/runs/${runId}/progress`);
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async createPipelineRun(params: CreatePipelineRunRequest): Promise<{ run_id: string; cloud_build_id: string }> {
    try {
      const response = await apiClient.post('/api/v1/pipeline/runs', params);
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async cancelPipelineRun(runId: string): Promise<{ success: boolean }> {
    try {
      const response = await apiClient.post(`/api/v1/pipeline/runs/${runId}/cancel`);
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async triggerPipelineStage(
    runId: string,
    stage: PipelineStageName,
    config?: PipelineRunConfig
  ): Promise<{ cloud_build_id: string }> {
    try {
      const response = await apiClient.post(`/api/v1/pipeline/runs/${runId}/stages/${stage}/trigger`, { config });
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  // =========================================================================
  // Content Management (Curated by Tripsby)
  // =========================================================================

  async getContentList(params?: {
    status?: string;
    contentType?: string;
    limit?: number;
  }): Promise<ContentListItem[]> {
    try {
      const response = await apiClient.get<ContentListItem[]>('/api/v1/content', { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async getContentStats(): Promise<ContentStats> {
    try {
      const response = await apiClient.get<ContentStats>('/api/v1/content/stats');
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async getContentItem(contentType: string, slug: string): Promise<ContentDetailItem> {
    try {
      const response = await apiClient.get<ContentDetailItem>(`/api/v1/content/${contentType}/${slug}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async submitContentReview(data: {
    contentType: string;
    slug: string;
    citySlug?: string;
  }): Promise<{ status: string; newStatus: string }> {
    try {
      const response = await apiClient.post('/api/v1/content/review', data);
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async approveContent(data: {
    contentType: string;
    slug: string;
    citySlug?: string;
    approved: boolean;
    feedback?: string;
  }): Promise<{ status: string; newStatus: string; approved: boolean }> {
    try {
      const response = await apiClient.post('/api/v1/content/approve', data);
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async publishContent(data: {
    contentType: string;
    slug: string;
    citySlug?: string;
    scheduledDate?: string;
  }): Promise<{ status: string; newStatus: string }> {
    try {
      const response = await apiClient.post('/api/v1/content/publish', data);
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async generateContent(data: {
    topic: string;
  }): Promise<{ status: string; editorial_slug?: string; verification_passed: boolean; teaser: string }> {
    try {
      const response = await apiClient.post('/api/v1/content/generate', data, {
        timeout: 420000, // 7 min — blog generation takes 3-6 min
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  // =========================================================================
  // Guide Review (Postgres-stored destination guides)
  // =========================================================================

  async getGuideSlugs(params?: { publishedOnly?: boolean }): Promise<string[]> {
    try {
      const response = await apiClient.get<string[]>('/api/v1/guides', {
        params: params?.publishedOnly ? { published_only: true } : undefined,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async getGuide(slug: string): Promise<GuideDetail> {
    try {
      const response = await apiClient.get<GuideDetail>(`/api/v1/guides/${slug}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async getGuideReview(slug: string): Promise<GuideReviewHistoryResponse> {
    try {
      const response = await apiClient.get<GuideReviewHistoryResponse>(`/api/v1/guides/${slug}/review`);
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async reviewGuide(
    slug: string,
    data: {
      decision: GuideReviewDecision;
      reasons: GuideReviewReason[];
      note?: string;
    }
  ): Promise<GuideReviewRecord> {
    try {
      const response = await apiClient.post<GuideReviewRecord>(`/api/v1/guides/${slug}/review`, data);
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  // Publish gate (Guide roadmap G3). The reviewer is never sent: the backend
  // maps the signed-in user to an editorial reviewer name.
  async getGuidePublishGates(
    slug: string,
    options?: { confirmSuperlativeTitle?: boolean }
  ): Promise<GuidePublishGates> {
    try {
      const response = await apiClient.get<GuidePublishGates>(
        `/api/v1/guides/${slug}/publish-gates`,
        {
          params: options?.confirmSuperlativeTitle ? { confirmSuperlativeTitle: true } : undefined,
        }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async publishGuide(
    slug: string,
    data: { confirmSuperlativeTitle?: boolean; note?: string }
  ): Promise<GuidePublishResult> {
    try {
      const response = await apiClient.post<GuidePublishResult>(
        `/api/v1/guides/${slug}/publish`,
        data
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async unpublishGuide(slug: string, data: { reason: string }): Promise<{ status: string }> {
    try {
      const response = await apiClient.post<{ status: string }>(
        `/api/v1/guides/${slug}/unpublish`,
        data
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  // Fact-check false-positive dismissals (Curated-By-Blog D-2026-09-30-02). As with
  // publish, the reviewer is never sent: the backend maps the signed-in user to
  // an editorial reviewer name (403 when they are not one). `reason` is required,
  // 10 to 500 characters.
  async dismissGuideFactCheckFinding(
    slug: string,
    data: { findingId: string; reason: string }
  ): Promise<GuideFactCheckDismissResult> {
    try {
      const response = await apiClient.post<GuideFactCheckDismissResult>(
        `/api/v1/guides/${slug}/fact-check/dismiss`,
        data
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async undismissGuideFactCheckFinding(
    slug: string,
    data: { findingId: string }
  ): Promise<GuideFactCheckUndismissResult> {
    try {
      const response = await apiClient.post<GuideFactCheckUndismissResult>(
        `/api/v1/guides/${slug}/fact-check/undismiss`,
        data
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },
};

// =============================================================================
// Media Library Types
// =============================================================================

export interface MediaFile {
  name: string;
  path: string;
  size: number;
  content_type: string;
  file_type: 'image' | 'video' | 'audio' | 'document' | 'file';
  updated: string | null;
  public_url: string;
  is_protected: boolean;
}

export interface MediaFolder {
  name: string;
  path: string;
  is_protected: boolean;
}

export interface MediaListResponse {
  current_path: string;
  folders: MediaFolder[];
  files: MediaFile[];
  total_files: number;
  total_folders: number;
}

export interface MediaUploadResponse {
  upload_url: string;
  public_url: string;
  blob_path: string;
  expires_in_minutes: number;
}

// =============================================================================
// Asset Version Types
// =============================================================================

export interface AssetVersion {
  id: string;
  original_path: string;
  version_path: string;
  asset_type: 'video' | 'poster' | 'avatar';
  filename: string;
  created_at: string;
  created_by: string;
  size: number;
  content_type: string;
  public_url: string;
  is_pre_restore_backup?: boolean;
}

// =============================================================================
// Pipeline Management Types
// =============================================================================

export type PipelineStageStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled' | 'skipped';
export type PipelineRunStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
export type PipelineStageName = 'scan' | 'process' | 'image_download' | 'image_embed';
export type PipelineMode = 'full' | 'stage';
export type PipelineEnvironment = 'prod' | 'staging';

export interface PipelineStageCheckpoint {
  total?: number;
  processed?: number;
  skipped_unchanged?: number;
  ers_failed?: number;
  timestamp?: string;
  last_hotel_id?: string;
}

export interface PipelineStageSummary {
  total?: number;
  processed?: number;
  skipped_unchanged?: number;
  ers_resolved?: number;
  ers_failed?: number;
  db_writes?: number;
  embedded?: number;
  golden_assembled?: number;
  errors?: number;
  rate_per_s?: number;
  elapsed_s?: number;
}

export interface PipelineStage {
  status: PipelineStageStatus;
  cloud_build_id?: string;
  cloud_build_status?: string;
  started_at?: string;
  completed_at?: string;
  summary?: PipelineStageSummary;
  checkpoint?: PipelineStageCheckpoint;
  log_url?: string;
}

export interface PipelineRunConfig {
  limit?: string;
  country_allowlist?: string;
  exclude_countries?: string;
  image_type?: 'all' | 'hotel' | 'room';
  include_place_id?: boolean;
  scan_tasks?: number;
  process_tasks?: number;
  img_dl_tasks?: number;
  img_embed_tasks?: number;
}

export interface PipelineRun {
  id: string;
  run_id: string;
  environment: PipelineEnvironment;
  trigger_type: 'manual' | 'scheduled';
  triggered_by: string;
  status: PipelineRunStatus;
  created_at: string;
  updated_at: string;
  cloud_build_id?: string;
  stages: Record<PipelineStageName, PipelineStage>;
  config: PipelineRunConfig;
}

export interface PipelineProgress {
  run_id: string;
  status: PipelineRunStatus;
  elapsed_seconds?: number;
  stages: Record<PipelineStageName, PipelineStage>;
  config: PipelineRunConfig;
}

export interface BatchPreset {
  name: string;
  limit?: string;
  country_allowlist?: string;
  exclude_countries?: string;
  image_type?: string;
}

export interface PipelineConfig {
  schedule_enabled: boolean;
  schedule_days: number[];  // 0=Sun, 1=Mon, ..., 6=Sat
  schedule_time: string;    // HH:MM in UTC
  schedule_environment: PipelineEnvironment;
  default_config: PipelineRunConfig;
  batch_presets: BatchPreset[];
  updated_at?: string;
  updated_by?: string;
}

export interface CreatePipelineRunRequest {
  mode: PipelineMode;
  stage?: PipelineStageName;
  environment: PipelineEnvironment;
  config?: PipelineRunConfig;
  preset?: string;
}

// =============================================================================
// Content Management Types (Curated by Tripsby)
// =============================================================================

export type ContentStatus = 'draft' | 'in_review' | 'approved' | 'scheduled' | 'published';
export type ContentType = 'city' | 'location' | 'editorial';

export interface ContentListItem {
  slug: string;
  title: string;
  contentType: ContentType;
  status: ContentStatus;
  updatedAt?: string;
  generatedBy?: string;
  approvedBy?: string;
  heroImage?: string;
  citySlug?: string;
}

export interface ContentStats {
  draft: number;
  in_review: number;
  approved: number;
  scheduled: number;
  published: number;
  total: number;
}

export interface ContentDetailItem {
  slug: string;
  title?: string;
  name?: string;
  status: ContentStatus;
  updatedAt?: string;
  generatedBy?: string;
  approvedBy?: string;
  publishedAt?: string;
  heroImage?: string;
  heroImages?: string[];
  subtitle?: string;
  sections?: Array<{ type: string; heading: string; content: string; image?: string }>;
  author?: string;
  tags?: string[];
  timeToRead?: string;
  template?: string;
  // City guide fields
  about?: { title: string; description: string; image?: string };
  highlights?: Array<{ title: string; description: string; image?: string }>;
  stats?: Record<string, string | string[]>;
  rankings?: Record<string, number>;
  // Location fields
  citySlug?: string;
  category?: string;
  address?: Record<string, string>;
  rating?: number;
  priceRange?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

// =============================================================================
// Guide Review Types (Postgres-stored destination guides — Content API)
//
// Distinct from ContentListItem/ContentDetailItem above, which model the
// Firestore-backed editorial/city/location content. Guides come from the
// Curated-By-Blog pipeline (scripts/batch_compose.py) and are keyed only by
// slug; GET /guides returns just the slug array, so the guide list view
// fetches per-slug detail to get title/status/QA.
// =============================================================================

export type GuideReviewDecision = 'approve' | 'reject' | 'request_changes';

// Taxonomy from the Guide Quality Roadmap, decision D4. 'other' requires a note.
export const GUIDE_REVIEW_REASONS = [
  'not_a_venue',
  'too_thin',
  'generic_copy',
  'wrong_chapter',
  'bad_address',
  'wrong_photo',
  'layout',
  'factual',
  'tone',
  'sensitive_topic',
  'other',
] as const;

export type GuideReviewReason = (typeof GUIDE_REVIEW_REASONS)[number];

export const GUIDE_REVIEW_REASON_LABELS: Record<GuideReviewReason, string> = {
  not_a_venue: 'Not a venue',
  too_thin: 'Too thin',
  generic_copy: 'Generic copy',
  wrong_chapter: 'Wrong chapter',
  bad_address: 'Bad address',
  wrong_photo: 'Wrong photo',
  layout: 'Layout',
  factual: 'Factual',
  tone: 'Tone',
  sensitive_topic: 'Sensitive topic',
  other: 'Other',
};

export interface GuideQaReport {
  ok: boolean;
  failures: string[];
  picks?: number;
  imaged?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface GuideJudgeReport {
  verdict?: 'publish' | 'fix' | 'kill';
  score?: number | null;
  one_line?: string;
  the_tell?: string;
  what_works?: string[];
  what_fails?: string[];
  findings?: string[];
}

export interface GuideReviewRecord {
  // Review verdicts carry `decision`; publish/unpublish entries (G3) share the
  // same history and carry `action` instead.
  decision?: GuideReviewDecision;
  action?: 'publish' | 'unpublish';
  reasons?: GuideReviewReason[];
  note?: string;
  reason?: string;
  reviewer: string;
  // On a publish entry: any named reviewer may publish another's approval.
  publishedBy?: string;
  approvedBy?: string;
  contentHash?: string;
  at?: string;
  timestamp?: string;
}

// Publish gate (G3). `status` is per gate: only `fail` blocks publishing;
// `advisory` means "would fail, but does not block".
export type GuideGateStatus = 'pass' | 'fail' | 'advisory';

export interface GuidePublishGate {
  gate: string;
  label: string;
  status: GuideGateStatus;
  required: boolean;
  detail: string;
}

// Fact-check findings (Content API `publish-gates` -> `factCheck`). `blocking`
// findings stop publishing until a reviewer dismisses them as verified false
// positives; `review_items` never block.
export type GuideFactCheckCategory =
  | 'closed'
  | 'card_mismatch'
  | 'place_mismatch'
  | 'contradicted';

export interface GuideFactCheckDismissal {
  reviewer: string;
  reason: string;
  at: string;
  claim: string;
  contentHash: string;
}

export interface GuideFactCheckBlockingFinding {
  findingId: string;
  category: GuideFactCheckCategory;
  venue: string;
  pick_id: string;
  chapter: string;
  type: string;
  claim: string;
  correction: string;
  source_url: string;
  severity: string;
  reason: string;
  // True only while the dismissal counts (same content it was made on).
  dismissed: boolean;
  // A stored dismissal whose content has since changed: the finding blocks again.
  dismissalExpired: boolean;
  // The stored record, also when it has expired.
  dismissal: GuideFactCheckDismissal | null;
}

export interface GuideFactCheckReviewItem {
  findingId: string;
  venue: string;
  pick_id: string;
  chapter: string;
  kind: string;
  type: string;
  level: string;
  severity: string;
  claim: string;
  correction: string;
  source_url: string;
  reason: string;
}

export interface GuideFactCheck {
  present: boolean;
  verdict: string | null;
  checkedAt: string | null;
  counts: { blocking: number; dismissed: number; open: number; review: number };
  blocking: GuideFactCheckBlockingFinding[];
  review_items: GuideFactCheckReviewItem[];
}

export interface GuideFactCheckDismissResult {
  slug: string;
  findingId: string;
  dismissal: GuideFactCheckDismissal;
}

export interface GuideFactCheckUndismissResult {
  slug: string;
  findingId: string;
  removed: GuideFactCheckDismissal;
  undismissedBy: string;
}

export interface GuidePublishGates {
  slug: string;
  status: string;
  contentHash: string;
  authoredBy: 'human' | 'ai';
  picks: number;
  superlativeTitle: boolean;
  reviewers: string[];
  gates: GuidePublishGate[];
  failed: { gate: string; detail: string }[];
  canPublish: boolean;
  canUnpublish: boolean;
  // The fact-check's findings with their dismissal status. Absent when the
  // Content API predates dismissals.
  factCheck?: GuideFactCheck;
  // Added by the Command Center backend: the signed-in user's editorial
  // reviewer name, or null when they are not one.
  me: { email: string; reviewer: string | null };
}

export interface GuidePublishResult {
  slug: string;
  status: string;
  publishedAt: string | null;
  contentHash: string;
}

export interface GuideReviewHistoryResponse {
  review: GuideReviewRecord | null;
  review_history: GuideReviewRecord[];
}

export interface GuideDetail {
  slug: string;
  title?: string;
  name?: string;
  draft?: boolean;
  status?: string;
  qa?: {
    report?: GuideQaReport;
    judge?: GuideJudgeReport;
    trace_id?: string;
  };
  review?: GuideReviewRecord | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

// =============================================================================
// Editorial CMS Types (Content Command Center)
// =============================================================================

export type EditorialTemplate = 'article' | 'guide' | 'list' | 'collection';
export type ContentGenerator = 'ai' | 'human';
export type LocationCategory = 'hotels' | 'restaurants' | 'bars' | 'attractions' | 'shopping';

export interface SeoMeta {
  title: string;
  description: string;
  ogImage?: string;
}

export interface EditorialSection {
  type: string;
  heading: string;
  content: string;
  image?: string;
}

export interface FeaturedItem {
  type: string;
  slug: string;
  citySlug?: string;
  displayOrder: number;
}

export interface EditorialListItem {
  slug: string;
  title: string;
  subtitle: string;
  heroImage: string;
  thumbnailImage: string;
  template: EditorialTemplate;
  author?: string;
  tags: string[];
  timeToRead?: string;
  status?: ContentStatus;
  updatedAt?: string;
  publishedAt?: string;
}

export interface EditorialPage {
  slug: string;
  title: string;
  subtitle: string;
  heroImage: string;
  thumbnailImage: string;
  template: EditorialTemplate;
  sections: EditorialSection[];
  featuredItems: FeaturedItem[];
  seo: SeoMeta;
  author?: string;
  authorAvatar?: string;
  tags: string[];
  timeToRead?: string;
  status: ContentStatus;
  scheduledPublishDate?: string;
  publishedAt?: string;
  generatedBy: ContentGenerator;
  reviewedBy?: string;
  approvedBy?: string;
  generatedAt?: string;
  updatedAt?: string;
}

export interface EditorialCreateRequest {
  slug: string;
  title: string;
  subtitle?: string;
  heroImage?: string;
  thumbnailImage?: string;
  template?: EditorialTemplate;
  sections?: EditorialSection[];
  featuredItems?: FeaturedItem[];
  seo?: SeoMeta;
  author?: string;
  authorAvatar?: string;
  tags?: string[];
  timeToRead?: string;
  generatedBy?: ContentGenerator;
}

// =============================================================================
// City & Location Types
// =============================================================================

export interface GeoCode {
  lat: number;
  long: number;
}

export interface CityStats {
  airports?: string[];
  population?: string;
  currency?: string;
  area?: string;
  language?: string;
  timezone?: string;
}

export interface CityHighlight {
  title: string;
  description: string;
  image?: string;
}

export interface CityEvent {
  name: string;
  date: string;
  description: string;
  image?: string;
  location?: string;
}

export interface CityRankings {
  overall?: number;
  food?: number;
  nightlife?: number;
  culture?: number;
  architecture?: number;
}

export interface CityAbout {
  title: string;
  description: string;
  image?: string;
}

export interface CityListItem {
  slug: string;
  name: string;
  subtitle: string;
  heroImage: string;
  geoCode: GeoCode;
}

export interface CityPage {
  slug: string;
  name: string;
  subtitle: string;
  heroImages: string[];
  geoCode: GeoCode;
  stats: CityStats;
  highlights: CityHighlight[];
  rankings: CityRankings;
  about: CityAbout;
  events: CityEvent[];
  seo?: SeoMeta;
  status: ContentStatus;
  scheduledPublishDate?: string;
  publishedAt?: string;
  generatedBy: ContentGenerator;
  reviewedBy?: string;
  approvedBy?: string;
  generatedAt?: string;
  updatedAt?: string;
}

export interface LocationAddress {
  line1?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
}

export interface LocationHighlight {
  title: string;
  description: string;
}

export interface LocationItem {
  slug: string;
  citySlug: string;
  category: LocationCategory;
  name: string;
  description: string;
  heroImage?: string;
  images: string[];
  address: LocationAddress;
  geoCode: GeoCode;
  rating?: number;
  reviewCount?: number;
  priceRange?: string;
  cuisine?: string;
  facilities?: string[];
  highlights: LocationHighlight[];
  nearbyLocationSlugs?: string[];
  status: ContentStatus;
  scheduledPublishDate?: string;
  publishedAt?: string;
  generatedBy: ContentGenerator;
  reviewedBy?: string;
  approvedBy?: string;
  generatedAt?: string;
  updatedAt?: string;
}

export interface LocationListResponse {
  citySlug: string;
  cityName: string;
  category: LocationCategory;
  locations: LocationItem[];
}

// =============================================================================
// Content API Client (Curated-By Content API)
// =============================================================================

const CONTENT_API_URL = import.meta.env.VITE_CONTENT_API_URL as string;

const contentApiClient = axios.create({
  baseURL: CONTENT_API_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

export const contentApi = {
  async getEditorials(): Promise<EditorialListItem[]> {
    try {
      // Route through the authed backend proxy (apiClient), not the direct
      // Content API client: the Content API's Cloud Run ingress only accepts
      // the backend's IAM identity, so a tokenless browser call is rejected 403.
      // status=all includes drafts, which the review workflow needs.
      const response = await apiClient.get<ContentListItem[]>('/api/v1/content', {
        params: { contentType: 'editorial', status: 'all' },
      });
      return (response.data ?? []).map((c): EditorialListItem => ({
        slug: c.slug,
        title: c.title,
        subtitle: '',
        heroImage: c.heroImage ?? '',
        thumbnailImage: c.heroImage ?? '',
        template: 'guide' as EditorialTemplate,
        tags: [],
        status: c.status,
        updatedAt: c.updatedAt,
      }));
    } catch (error) {
      throw new APIError(
        ErrorCode.SERVICE_UNAVAILABLE,
        'Failed to fetch editorials from Content API',
        (error as AxiosError)?.response?.status || 500
      );
    }
  },

  async getEditorial(slug: string): Promise<EditorialPage> {
    try {
      const response = await apiClient.get<EditorialPage>(`/api/v1/content/editorial/${slug}`);
      return response.data;
    } catch (error) {
      const status = (error as AxiosError)?.response?.status;
      if (status === 404) {
        throw new APIError(ErrorCode.NOT_FOUND, `Editorial "${slug}" not found`, 404);
      }
      throw new APIError(
        ErrorCode.SERVICE_UNAVAILABLE,
        'Failed to fetch editorial from Content API',
        status || 500
      );
    }
  },

  async saveEditorial(data: EditorialCreateRequest): Promise<EditorialPage> {
    try {
      const response = await apiClient.post<EditorialPage>('/api/v1/content/editorials', data);
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async submitForReview(
    contentType: ContentType,
    slug: string,
    citySlug?: string
  ): Promise<{ status: string; newStatus: string }> {
    try {
      const response = await apiClient.post('/api/v1/content/workflow/review', {
        content_type: contentType,
        slug,
        city_slug: citySlug,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async approveContent(
    contentType: ContentType,
    slug: string,
    approvedBy: string,
    approved: boolean = true,
    feedback?: string,
    citySlug?: string
  ): Promise<{ status: string; newStatus: string; approved: boolean }> {
    try {
      const response = await apiClient.post('/api/v1/content/workflow/approve', {
        content_type: contentType,
        slug,
        city_slug: citySlug,
        approved,
        feedback,
        approved_by: approvedBy,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async publishContent(
    contentType: ContentType,
    slug: string,
    scheduledDate?: string,
    citySlug?: string
  ): Promise<{ status: string; newStatus: string }> {
    try {
      const response = await apiClient.post('/api/v1/content/workflow/publish', {
        content_type: contentType,
        slug,
        city_slug: citySlug,
        scheduled_date: scheduledDate,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error as AxiosError<APIErrorResponse>);
    }
  },

  async getCities(): Promise<CityListItem[]> {
    try {
      const response = await contentApiClient.get<CityListItem[]>('/cities');
      return response.data;
    } catch (error) {
      throw new APIError(
        ErrorCode.SERVICE_UNAVAILABLE,
        'Failed to fetch cities from Content API',
        (error as AxiosError)?.response?.status || 500
      );
    }
  },

  async getCity(citySlug: string): Promise<CityPage> {
    try {
      const response = await contentApiClient.get<CityPage>(`/cities/${citySlug}`);
      return response.data;
    } catch (error) {
      const status = (error as AxiosError)?.response?.status;
      if (status === 404) {
        throw new APIError(ErrorCode.NOT_FOUND, `City "${citySlug}" not found`, 404);
      }
      throw new APIError(
        ErrorCode.SERVICE_UNAVAILABLE,
        'Failed to fetch city from Content API',
        status || 500
      );
    }
  },

  async getCityLocations(citySlug: string, category: LocationCategory): Promise<LocationListResponse> {
    try {
      const response = await contentApiClient.get<LocationListResponse>(`/cities/${citySlug}/${category}`);
      return response.data;
    } catch (error) {
      throw new APIError(
        ErrorCode.SERVICE_UNAVAILABLE,
        'Failed to fetch locations from Content API',
        (error as AxiosError)?.response?.status || 500
      );
    }
  },
};
